import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password });

// System config
export const getTimezoneConfig = () => api.get('/config/timezone');

// Users
export const getUsers = () => api.get('/users');
export const createUser = (userData) => api.post('/users', userData);
export const updateUser = (userId, userData) => api.put(`/users/${userId}`, userData);
export const deleteUser = (userId) => api.delete(`/users/${userId}`);
export const changePassword = (userId, passwordData) => api.put(`/users/${userId}/password`, passwordData);

// Empresas
export const getEmpresas = (assignedTo) => api.get('/empresas', { params: { assigned_to: assignedTo } });
export const getEmpresa = (empresaId) => api.get(`/empresas/${empresaId}`);
export const createEmpresa = (empresaData) => api.post('/empresas', empresaData);
export const updateEmpresa = (empresaId, empresaData) => api.put(`/empresas/${empresaId}`, empresaData);
export const deleteEmpresa = (empresaId) => api.delete(`/empresas/${empresaId}`);

// Contacts
export const addContact = (empresaId, contactData) => api.post(`/empresas/${empresaId}/contacts`, contactData);
export const updateContact = (contactId, contactData) => api.put(`/contacts/${contactId}`, contactData);
export const deleteContact = (contactId) => api.delete(`/contacts/${contactId}`);

// Notes
export const addNote = (empresaId, noteData, createdBy) => 
  api.post(`/empresas/${empresaId}/notes?created_by=${createdBy}`, noteData);
export const updateNote = (noteId, noteData) => api.put(`/notes/${noteId}`, noteData);
export const deleteNote = (noteId) => api.delete(`/notes/${noteId}`);

// Merge operations
export const findDuplicateEmpresas = () => api.get('/empresas/duplicates');
export const mergeEmpresas = (sourceId, targetId) => 
  api.post('/empresas/merge', { source_id: sourceId, target_id: targetId });
export const mergeContacts = (empresaId, sourceContactId, targetContactId) => 
  api.post(`/empresas/${empresaId}/contacts/merge`, { 
    empresa_id: empresaId, 
    source_contact_id: sourceContactId, 
    target_contact_id: targetContactId 
  });

// Empresa Documents
export const getEmpresaDocuments = (empresaId) => api.get(`/empresas/${empresaId}/documents`);
export const addEmpresaDocument = (empresaId, documentData) => 
  api.post(`/empresas/${empresaId}/documents`, documentData);
export const updateEmpresaDocument = (empresaId, documentId, documentData) => 
  api.put(`/empresas/${empresaId}/documents/${documentId}`, documentData);
export const deleteEmpresaDocument = (empresaId, documentId) => 
  api.delete(`/empresas/${empresaId}/documents/${documentId}`);
export const downloadEmpresaDocument = (empresaId, documentId) => 
  api.get(`/empresas/${empresaId}/documents/${documentId}/download`);

// Tasks
export const getTasks = (assignedTo, status) => 
  api.get('/tasks', { params: { assigned_to: assignedTo, status } });
export const createTask = (taskData, createdBy) => 
  api.post(`/tasks?created_by=${createdBy}`, taskData);
export const updateTask = (taskId, taskData) => api.put(`/tasks/${taskId}`, taskData);
export const deleteTask = (taskId) => api.delete(`/tasks/${taskId}`);

// Pipelines
export const getPipelines = () => api.get('/pipelines');
export const createPipeline = (pipelineData, createdBy) => 
  api.post(`/pipelines?created_by=${createdBy}`, pipelineData);
export const updatePipeline = (pipelineId, pipelineData) => api.put(`/pipelines/${pipelineId}`, pipelineData);
export const deletePipeline = (pipelineId) => api.delete(`/pipelines/${pipelineId}`);

// Deals
export const getDeals = (pipelineId, empresaId) => 
  api.get('/deals', { params: { pipeline_id: pipelineId, empresa_id: empresaId } });
export const createDeal = (dealData) => api.post('/deals', dealData);
export const updateDeal = (dealId, dealData) => api.put(`/deals/${dealId}`, dealData);
export const deleteDeal = (dealId) => api.delete(`/deals/${dealId}`);

// Evaluaciones
export const getEvaluaciones = (empresaId) => 
  api.get('/evaluaciones', { params: { empresa_id: empresaId } });
export const createEvaluacion = (evaluacionData, commissionRate = 35) => 
  api.post(`/evaluaciones?commission_rate=${commissionRate}`, evaluacionData);
export const updateEvaluacion = (evaluacionId, evaluacionData, commissionRate = 35) => 
  api.put(`/evaluaciones/${evaluacionId}?commission_rate=${commissionRate}`, evaluacionData);
export const deleteEvaluacion = (evaluacionId) => api.delete(`/evaluaciones/${evaluacionId}`);

// Stats
export const getStats = (userName) => api.get('/stats', { params: { user_name: userName } });

// Seed database
export const seedDatabase = () => api.post('/seed');
export const importFullData = () => api.post('/import-full-data');

// Reports
export const getSalesByPeriod = (period = 'monthly') => api.get('/reports/sales-by-period', { params: { period } });
export const getPipelineValue = () => api.get('/reports/pipeline-value');
export const getPerformanceByUser = () => api.get('/reports/performance-by-user');
export const getConversionByStage = () => api.get('/reports/conversion-by-stage');

// Deal Movement (Drag & Drop)
export const moveDeal = (dealId, newStageId, newStageName) => 
  api.put(`/deals/${dealId}/move`, { deal_id: dealId, new_stage_id: newStageId, new_stage_name: newStageName });

// Pipeline Stage Management
export const updatePipelineStages = (pipelineId, stages) => 
  api.put(`/pipelines/${pipelineId}/stages`, { stages });

// Email Templates
export const getEmailTemplates = () => api.get('/email-templates');
export const createEmailTemplate = (templateData, createdBy) => 
  api.post(`/email-templates?created_by=${createdBy}`, templateData);
export const updateEmailTemplate = (templateId, templateData) => 
  api.put(`/email-templates/${templateId}`, templateData);
export const deleteEmailTemplate = (templateId) => api.delete(`/email-templates/${templateId}`);

// Email Sending
export const sendEmail = (emailData) => api.post('/emails/send', emailData);
export const sendEmailWithAttachments = async (emailData, files) => {
  const formData = new FormData();
  
  // Add text fields
  formData.append('sender_id', emailData.sender_id);
  formData.append('sender_name', emailData.sender_name);
  formData.append('contact_email', emailData.contact_email);
  formData.append('subject', emailData.subject);
  formData.append('body', emailData.body);
  
  if (emailData.empresa_id) formData.append('empresa_id', emailData.empresa_id);
  if (emailData.empresa_name) formData.append('empresa_name', emailData.empresa_name);
  if (emailData.contact_id) formData.append('contact_id', emailData.contact_id);
  if (emailData.contact_name) formData.append('contact_name', emailData.contact_name);
  if (emailData.template_id) formData.append('template_id', emailData.template_id);
  
  // Add template attachments as JSON (base64 encoded files from template)
  if (emailData.template_attachments && emailData.template_attachments.length > 0) {
    formData.append('template_attachments', JSON.stringify(emailData.template_attachments));
  }
  
  // Add manual file attachments
  for (const file of files) {
    formData.append('files', file);
  }
  
  return api.post('/emails/send-with-attachments', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};
export const getEmailLogs = (empresaId) => api.get('/emails/logs', { params: { empresa_id: empresaId } });

// API Keys Management
export const getAPIKeys = () => api.get('/api-keys');
export const createAPIKey = (keyData) => api.post('/api-keys', keyData);
export const deleteAPIKey = (keyId) => api.delete(`/api-keys/${keyId}`);

// Empresas with Sorting and Pagination
export const getEmpresasSorted = (sortBy = 'created_at', sortOrder = 'desc', category = null, assignedTo = null, page = 1, limit = 100) => 
  api.get('/empresas-sorted', { params: { sort_by: sortBy, sort_order: sortOrder, category, assigned_to: assignedTo, page, limit } });

// Empresas count for pagination
export const getEmpresasCount = (assignedTo = null, category = null) =>
  api.get('/empresas/count', { params: { assigned_to: assignedTo, category } });

// Roles Management
export const getRoles = () => api.get('/roles');
export const createRole = (roleData) => api.post('/roles', roleData);
export const updateRole = (roleId, roleData) => api.put(`/roles/${roleId}`, roleData);
export const deleteRole = (roleId) => api.delete(`/roles/${roleId}`);

// User Commission Configuration
export const getAllUserCommissions = () => api.get('/user-commissions');
export const getUserCommissions = (userId) => api.get(`/user-commissions/${userId}`);
export const updateUserCommissions = (userId, config) => api.put(`/user-commissions/${userId}`, config);
export const calculateUserCommission = (userId, pipelineId, serviceName, amount) => 
  api.get(`/user-commissions/${userId}/calculate`, { 
    params: { pipeline_id: pipelineId, service_name: serviceName, amount } 
  });
export const getEvaluationServices = () => api.get('/evaluation-services');

// Contact Notes
export const addContactNote = (contactId, noteData, createdBy) => 
  api.post(`/contacts/${contactId}/notes?created_by=${createdBy}`, noteData);
export const deleteContactNote = (contactId, noteId) => 
  api.delete(`/contacts/${contactId}/notes/${noteId}`);

// Import/Export
export const exportAllData = () => api.get('/export/all');
export const exportEmpresas = () => api.get('/export/empresas');
export const importAllData = (data, merge = false) => 
  api.post(`/import/all?merge=${merge}`, data);
export const importFromCSV = (data) => api.post('/import/csv', data);

// Delete completed tasks
export const deleteCompletedTasks = () => api.delete('/tasks/completed');

// Blacklist
export const toggleBlacklist = (empresaId, reason) => 
  api.put(`/empresas/${empresaId}/blacklist${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`);

// Multi-type CSV Import (for Canva Code Data format)
export const importFromCSVMultitype = (records) => 
  api.post('/import/csv-multitype', { records });

// Quote Configuration
export const getQuoteConfig = () => api.get('/config/quotes');
export const updateQuoteConfig = (config) => api.put('/config/quotes', config);

// ==================== GOOGLE INTEGRATION ====================

// Google OAuth
export const getGoogleConfig = () => api.get('/google/config');
export const saveGoogleConfig = (config) => api.post('/google/config', config);
export const deleteGoogleConfig = () => api.delete('/google/config');
export const getGoogleStatus = (userId) => api.get(`/google/status/${userId}`);
export const startGoogleUserAuth = (userId, redirectUri) => 
  api.get('/google/auth/user/start', { params: { user_id: userId, redirect_uri: redirectUri } });
export const startGoogleCompanyAuth = (userId, redirectUri) => 
  api.get('/google/auth/company/start', { params: { user_id: userId, redirect_uri: redirectUri } });
export const processGoogleCallback = (code, state, redirectUri) => 
  api.get('/google/auth/callback', { params: { code, state, redirect_uri: redirectUri } });
export const disconnectGoogle = (connectionType, userId) => 
  api.delete(`/google/disconnect/${connectionType}`, { params: { user_id: userId } });

// Gmail
export const sendGmail = (userId, emailData) => 
  api.post('/google/gmail/send', emailData, { params: { user_id: userId } });
export const listGmailMessages = (userId, maxResults = 20, query = null) => 
  api.get('/google/gmail/messages', { params: { user_id: userId, max_results: maxResults, query } });
export const getGmailMessage = (userId, messageId) => 
  api.get(`/google/gmail/messages/${messageId}`, { params: { user_id: userId } });
export const deleteGmailMessage = (userId, messageId) => 
  api.delete(`/google/gmail/messages/${messageId}`, { params: { user_id: userId } });
export const getGmailAttachment = (userId, messageId, attachmentId) => 
  api.get(`/google/gmail/messages/${messageId}/attachments/${attachmentId}`, { params: { user_id: userId } });

// Calendar
export const listCalendars = (userId) => 
  api.get('/google/calendar/list', { params: { user_id: userId } });
export const listCalendarEvents = (userId, calendarId = 'primary', timeMin = null, timeMax = null) => 
  api.get('/google/calendar/events', { params: { user_id: userId, calendar_id: calendarId, time_min: timeMin, time_max: timeMax } });
export const createCalendarEvent = (userId, eventData) => 
  api.post('/google/calendar/events', eventData, { params: { user_id: userId } });
export const deleteCalendarEvent = (userId, eventId, calendarId = 'primary') => 
  api.delete(`/google/calendar/events/${eventId}`, { params: { user_id: userId, calendar_id: calendarId } });

// Task-Calendar Sync
export const syncTasksFromCalendar = (userId) => 
  api.post('/tasks/sync-from-calendar', null, { params: { user_id: userId } });
export const syncTaskToCalendar = (taskId) => 
  api.post(`/tasks/${taskId}/sync-to-calendar`);
export const syncAllTasksToCalendar = (userId) => 
  api.post('/tasks/sync-all-to-calendar', null, { params: { user_id: userId } });

// Drive
export const uploadToDrive = (file, folderId = null) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/google/drive/upload', formData, { 
    headers: { 'Content-Type': 'multipart/form-data' },
    params: { folder_id: folderId }
  });
};
export const listDriveFiles = (folderId = null, query = null, maxResults = 50) => 
  api.get('/google/drive/files', { params: { folder_id: folderId, query, max_results: maxResults } });
export const createDriveFolder = (name, parentId = null) => 
  api.post('/google/drive/folder', null, { params: { name, parent_id: parentId } });
export const shareDriveFile = (fileId, email, role = 'reader') => 
  api.post(`/google/drive/share/${fileId}`, null, { params: { email, role } });

// Sheets
export const createSpreadsheet = (title) => 
  api.post('/google/sheets/create', null, { params: { title } });
export const readSpreadsheet = (spreadsheetId, range) => 
  api.get(`/google/sheets/${spreadsheetId}`, { params: { range } });
export const writeSpreadsheet = (spreadsheetId, range, values) => 
  api.post('/google/sheets/write', { spreadsheet_id: spreadsheetId, range, values });

// Docs
export const createDocument = (title) => 
  api.post('/google/docs/create', null, { params: { title } });
export const createDocFromTemplate = (templateId, title, placeholders = {}) => 
  api.post('/google/docs/from-template', { template_id: templateId, title, placeholders });

// ==================== GEMINI AI ====================
export const aiGenerateEmail = (data) => api.post('/ai/generate-email', data);
export const aiGenerateProposal = (data) => api.post('/ai/generate-proposal', data);
export const aiAnalyzeData = (data) => api.post('/ai/analyze-data', data);
export const aiAssistant = (message, context = null, history = null) => 
  api.post('/ai/assistant', { message, context, conversation_history: history });
export const aiSuggestFollowUp = (data) => api.post('/ai/suggest-followup', data);
export const aiImproveText = (text, improvementType = 'profesional') => 
  api.post('/ai/improve-text', { text, improvement_type: improvementType });

// ==================== GAMMA AI (PROPOSALS) ====================
export const getGammaStatus = () => api.get('/gamma/status');
export const getGammaThemes = () => api.get('/gamma/themes');
export const generateGammaProposal = (data) => api.post('/gamma/generate', data);
export const getGammaGeneration = (generationId) => api.get(`/gamma/generations/${generationId}`);
export const getGammaProposals = (empresaId = null) => 
  api.get('/gamma/proposals', { params: { empresa_id: empresaId } });
export const deleteGammaProposal = (proposalId) => api.delete(`/gamma/proposals/${proposalId}`);

// ==================== PUBLIC FORMS ====================
export const getFormTemplates = () => api.get('/forms/templates');
export const getForms = () => api.get('/forms');
export const getForm = (formId) => api.get(`/forms/${formId}`);
export const createForm = (formData) => api.post('/forms', formData);
export const updateForm = (formId, formData) => api.put(`/forms/${formId}`, formData);
export const deleteForm = (formId) => api.delete(`/forms/${formId}`);
export const getFormSubmissions = (formId, limit = 100) => 
  api.get(`/forms/${formId}/submissions`, { params: { limit } });
export const getFormEmbedCode = (formId, baseUrl = null) => 
  api.get(`/forms/${formId}/embed-code`, { params: { base_url: baseUrl } });

// Public form endpoints (no auth)
export const getPublicForm = (formId) => api.get(`/public/forms/${formId}`);
export const submitPublicForm = (formId, data) => 
  api.post(`/public/forms/${formId}/submit`, data);

// ==================== AI AGENTS ====================
export const getAgents = (status = null, type = null) => 
  api.get('/agents', { params: { status, type } });
export const getAgent = (agentId) => api.get(`/agents/${agentId}`);
export const createAgent = (agentData) => api.post('/agents', agentData);
export const updateAgent = (agentId, agentData) => api.put(`/agents/${agentId}`, agentData);
export const deleteAgent = (agentId) => api.delete(`/agents/${agentId}`);

// Agent Actions
export const getAgentActions = (agentId, status = null) => 
  api.get(`/agents/${agentId}/actions`, { params: { status } });
export const getPendingActions = () => api.get('/agents/actions/pending');
export const createAgentAction = (agentId, actionData) => 
  api.post(`/agents/${agentId}/actions`, actionData);
export const approveAction = (actionId, approvalData) => 
  api.post(`/agents/actions/${actionId}/approve`, approvalData);
export const executeAction = (actionId) => 
  api.post(`/agents/actions/${actionId}/execute`);

// Agent AI Generation
export const generateAgentMessage = (agentId, requestData) => 
  api.post(`/agents/${agentId}/generate`, requestData);

// Agent Training
export const getAgentTraining = (agentId, type = null, category = null) => 
  api.get(`/agents/${agentId}/training`, { params: { type, category } });
export const addAgentTraining = (agentId, trainingData) => 
  api.post(`/agents/${agentId}/training`, trainingData);
export const updateAgentTraining = (agentId, trainingId, trainingData) => 
  api.put(`/agents/${agentId}/training/${trainingId}`, trainingData);
export const deleteAgentTraining = (agentId, trainingId) => 
  api.delete(`/agents/${agentId}/training/${trainingId}`);
export const updateAgentInstructions = (agentId, instructions) => 
  api.put(`/agents/${agentId}/instructions`, instructions);

// Agent Immediate Execution
export const executeAgentImmediate = (agentId, actionData) => 
  api.post(`/agents/${agentId}/execute-immediate`, actionData);

// Agent Drive Integration
export const setupAgentDrive = (agentId, userId) => 
  api.post(`/agents/${agentId}/drive/setup`, null, { params: { user_id: userId } });
export const syncAgentDrive = (agentId, userId) => 
  api.post(`/agents/${agentId}/drive/sync`, null, { params: { user_id: userId } });

// Agent Analytics
export const getAgentAnalytics = (agentId, days = 30) => 
  api.get(`/agents/${agentId}/analytics`, { params: { days } });

// Agent Webhook (for n8n)
export const triggerAgentWebhook = (agentId, data) => 
  api.post(`/agents/${agentId}/webhook`, data);

// Agent Test Webhook
export const testAgentWebhook = (agentId, webhookUrl = null) => 
  api.post(`/agents/${agentId}/test-webhook`, null, { params: { webhook_url: webhookUrl } });

// Agent Webhook Logs
export const getAgentWebhookLogs = (agentId, limit = 20) => 
  api.get(`/agents/${agentId}/webhook-logs`, { params: { limit } });

// Export all functions as methods on api object for backwards compatibility
api.getAgents = getAgents;
api.getAgent = getAgent;
api.createAgent = createAgent;
api.updateAgent = updateAgent;
api.deleteAgent = deleteAgent;
api.getAgentActions = getAgentActions;
api.getPendingActions = getPendingActions;
api.createAgentAction = createAgentAction;
api.approveAction = approveAction;
api.executeAction = executeAction;
api.generateAgentMessage = generateAgentMessage;
api.getAgentTraining = getAgentTraining;
api.addAgentTraining = addAgentTraining;
api.updateAgentTraining = updateAgentTraining;
api.deleteAgentTraining = deleteAgentTraining;
api.updateAgentInstructions = updateAgentInstructions;
api.executeAgentImmediate = executeAgentImmediate;
api.setupAgentDrive = setupAgentDrive;
api.syncAgentDrive = syncAgentDrive;
api.getAgentAnalytics = getAgentAnalytics;
api.triggerAgentWebhook = triggerAgentWebhook;
api.testAgentWebhook = testAgentWebhook;
api.getAgentWebhookLogs = getAgentWebhookLogs;
// Additional attachments used by AgentsPage
api.getEmpresas = getEmpresas;
api.getUsers = getUsers;

// ==================== AUTOMATIONS ====================
export const getAutomations = (isActive = null) => 
  api.get('/automations', { params: { is_active: isActive } });
export const getAutomation = (automationId) => api.get(`/automations/${automationId}`);
export const createAutomation = (automationData) => api.post('/automations', automationData);
export const updateAutomation = (automationId, automationData) => 
  api.put(`/automations/${automationId}`, automationData);
export const deleteAutomation = (automationId) => api.delete(`/automations/${automationId}`);
export const toggleAutomation = (automationId) => api.post(`/automations/${automationId}/toggle`);
export const runAutomation = (automationId, context = null) => 
  api.post(`/automations/${automationId}/run`, context);
export const getAutomationLogs = (automationId, limit = 20) => 
  api.get(`/automations/${automationId}/logs`, { params: { limit } });
export const getAutomationsConfig = () => api.get('/automations/config');
export const getAutomationResources = (userId = null) => 
  api.get('/automations/resources', { params: { user_id: userId } });

export default api;
